import { IEventorStateModel, IRosterSplitFit, IShiftGroup } from './eventor.models';

import { EaSortingDirection } from 'src/app/components/ea-button/ea-button.models';
import {
    EEventItemType,
    IGamePlayerShiftEvent,
    TGameEvent,
    TPlayerEvent,
    TShiftEvent
} from 'src/app/components/ea-event-item/ea-event.models';
import { EShiftSituationDirection } from 'src/app/components/ea-player-shifts/ea-player-shifts.models';
import { RosterListSortProperty } from 'src/app/components/ea-roster-list/ea-roster-list.models';
import {
    IGameEvent,
    IGameEventDefinition,
    IPlayerEvent,
    IPlayerEventDefinition,
    IPlayerEventShiftsApiResponse,
    IShiftEventDefinition
} from 'src/app/states/event/event.models';
import { IRosterPerson, IRosterPlayer } from 'src/app/states/roster/roster.models';
import { IStateSliceDefaults, StateSliceName } from 'src/app/states/store.models';
import { ITeam } from 'src/app/states/team/team.models';

/** Default pixel size of the roster split area */
export const SPLIT_DEFAULT_SIZE = 380;
/** Minimum pixel size of the roster split area when expanded */
export const SPLIT_MIN_SIZE = 240;
/** Maximum pixel size of the roster split area when expanded */
export const SPLIT_MAX_SIZE = 560;
/** Pixel size of the roster split area when collapsed (header height with padding) */
export const SPLIT_COLLAPSED_SIZE = 72;
/** Pixel height of the gutter between the roster and events panes */
export const SPLIT_GUTTER_SIZE = 16;
/**
 * Pixel height kept for the events pane when the viewport shrinks.
 * The roster pane gives up space first so this pane cannot collapse to 0.
 */
export const SPLIT_EVENTS_MIN_SIZE = 160;

/**
 * Fits the roster pane into the sidebar after the viewport changes.
 * The dragged size is kept when the sidebar is tall enough. When it is not,
 * the roster shrinks so the events pane keeps {@link SPLIT_EVENTS_MIN_SIZE}.
 */
export function fitRosterSplitToContainer(preferredSize: number, containerHeight: number): IRosterSplitFit {
    if (containerHeight <= 0) {
        return {
            size: Math.min(preferredSize, SPLIT_MAX_SIZE),
            minSize: SPLIT_MIN_SIZE,
            maxSize: SPLIT_MAX_SIZE
        };
    }

    const roomForRoster = containerHeight - SPLIT_GUTTER_SIZE - SPLIT_EVENTS_MIN_SIZE;
    const size = Math.min(
        preferredSize,
        SPLIT_MAX_SIZE,
        Math.max(roomForRoster, SPLIT_COLLAPSED_SIZE)
    );

    return {
        size,
        minSize: Math.min(SPLIT_MIN_SIZE, size),
        maxSize: Math.min(SPLIT_MAX_SIZE, Math.max(size, roomForRoster))
    };
}

export const eventorStateDefaults: IStateSliceDefaults<IEventorStateModel> = Object.freeze({
    sliceName: StateSliceName.EVENTOR,
    defaults: {
        selectedPlayerIdByTeamId: {},
        rosterListSortDirection: EaSortingDirection.ASC,
        selectedSortRosterListProperty: RosterListSortProperty.POSITION,
        eventingOverlayHidden: false,
        eventingOverlayDocked: false,
        queryParamEventId: undefined,
        selectedTeamIdForEvents: undefined,
        selectedToggleShiftsForEvent: true,
        selectedToggleUserCreatedEvents: false,
        clickedSurfaceCoord: null
    }
});

export const eventTypeOrder: Record<string, number> = {
    [EEventItemType.GAME]: 0,
    [EEventItemType.SHIFT]: 1,
    [EEventItemType.PLAYER]: 2
};

/**
 * Maps raw player shift events into a list of grouped shift events for each event.
 *
 * Shift-in and shift-out events that share the same `teamId`, `period`, and `frame` are
 * combined into a single `IGameAndPlayerEvent` entry, with the participating players
 * collected in `shiftsIn` and `shiftsOut`.

 */
// eslint-disable-next-line complexity
export function buildShiftEventsToGamePlayerShiftEvents(
    playerShiftEvents: IPlayerEventShiftsApiResponse[] | undefined,
    shiftEventDefinitionsById: Record<string, IShiftEventDefinition>,
    bothTeamPlayersById: Record<string, IRosterPlayer>,
    rosterPersonById: Record<string, IRosterPerson> | null | undefined,
    teamsById: Record<string, ITeam> | undefined,
    frameRate: number
): TShiftEvent[] {
    if (!playerShiftEvents || playerShiftEvents.length === 0) {
        return [];
    }

    const shiftGroups = new Map<string, IShiftGroup>();

    for (const shiftEvt of playerShiftEvents) {
        const key = `${shiftEvt.teamId}|${shiftEvt.period}|${shiftEvt.frame}`;

        let group = shiftGroups.get(key);
        if (!group) {
            group = { shiftEvent: shiftEvt, shiftsIn: [], shiftsOut: [] };
            shiftGroups.set(key, group);
        }

        const player = bothTeamPlayersById[shiftEvt.playerId];
        const person = rosterPersonById?.[shiftEvt.playerId];
        const playerInfo: { jerseyNum: string; name: string } = {
            jerseyNum: player?.jerseyNum ?? '#',
            name: person ? `${person.firstName} ${person.lastName}` : '',
        };

        const shiftType = shiftEventDefinitionsById[shiftEvt.playerShiftEventDefinitionId]?.name;

        if (shiftType === EShiftSituationDirection.IN) {
            group.shiftsIn.push(playerInfo);
        } else if (shiftType === EShiftSituationDirection.OUT) {
            group.shiftsOut.push(playerInfo);
        }
    }

    const result: TShiftEvent[] = [];
    for (const { shiftEvent, shiftsIn, shiftsOut } of shiftGroups.values()) {
        result.push({
            id: shiftEvent.id,
            definition: shiftEventDefinitionsById[shiftEvent.playerShiftEventDefinitionId]?.name,
            defId: shiftEvent.playerShiftEventDefinitionId,
            eventType: EEventItemType.SHIFT,
            period: shiftEvent.period,
            frame: shiftEvent.frame,
            createdBy: shiftEvent.createdBy,
            playerId: null,
            frameRate,
            teamId: shiftEvent.teamId,
            teamLogo: teamsById?.[shiftEvent.teamId]?.logoSource ?? '',
            shiftsIn,
            shiftsOut,
        });
    }

    return result;
}

/**
 * Maps raw player events into `IGamePlayerShiftEvent` entries, each
 * with definition label, flag names, player/team display info, and frame rate.
 */
// eslint-disable-next-line complexity
export function buildPlayerEventsToGamePlayerShiftEvents(
    playerEvents: IPlayerEvent[] | undefined,
    playerEventDefinitionsById: Record<string, IPlayerEventDefinition>,
    flagNamesById: Record<string, string>,
    bothTeamPlayersById: Record<string, IRosterPlayer>,
    rosterPersonById: Record<string, IRosterPerson> | null | undefined,
    teamsById: Record<string, ITeam> | undefined,
    frameRate: number
): TPlayerEvent[] {
    if (!playerEvents || playerEvents.length === 0) {
        return [];
    }

    const result: TPlayerEvent[] = [];
    for (const pe of playerEvents) {
        const person = rosterPersonById?.[pe.playerId];
        const player = bothTeamPlayersById[pe.playerId];
        const team = pe.teamId !== null ? teamsById?.[pe.teamId] : undefined;

        let flags = '';
        if (pe.flagIds) {
            const names: string[] = [];
            for (const flagId of pe.flagIds) {
                const name = flagNamesById[flagId];
                if (name !== undefined) names.push(name);
            }
            flags = names.join(', ');
        }

        result.push({
            id: pe.id,
            definition: playerEventDefinitionsById[pe.defId]?.label,
            flags,
            defId: pe.defId,
            eventType: EEventItemType.PLAYER,
            period: pe.period,
            frame: pe.frame,
            createdBy: pe.createdBy,
            periodTime: pe.periodTime ?? undefined,
            playerId: pe.playerId,
            frameRate,
            teamId: pe.teamId,
            playerName: person ? `${person.firstName} ${person.lastName}` : '',
            jerseyNum: player?.jerseyNum ?? '#',
            teamDisplayName: team?.displayName ?? '',
            teamLogo: team?.logoSource ?? '',
            xAdjCoord: pe.xAdjCoord,
            xCoord: pe.xCoord,
            yAdjCoord: pe.yAdjCoord,
            yCoord: pe.yCoord,
        });
    }

    return result;
}
/**
 * Maps raw game events into `IGamePlayerShiftEvent` entries, attaching the
 * definition name and frame rate for display.
 */
// eslint-disable-next-line complexity
export function buildGameEventsToGamePlayerShiftEvents(
    gameEvents: IGameEvent[] | undefined,
    gameEventDefinitionsById: Record<string, IGameEventDefinition>,
    bothTeamPlayersById: Record<string, IRosterPlayer>,
    teamsById: Record<string, ITeam> | undefined,
    frameRate: number,
    periodTimeDuration: number,
    gameClockGoesDown: boolean
): TGameEvent[] {
    if (!gameEvents || gameEvents.length === 0) {
        return [];
    }

    const result: TGameEvent[] = [];
    for (const ge of gameEvents) {
        const teamDisplayName = ge.teamId ? teamsById?.[ge.teamId]?.displayName : '';
        const gameDef = gameEventDefinitionsById[ge.defId];
        const player = bothTeamPlayersById?.[ge.playerId];
        const filterOutGameDefType = gameDef?.type !== 'none' && gameDef?.type !== 'end' && gameDef?.type !== 'start';
        const definition = `${teamDisplayName} ${ge.playerId ? `#${player?.jerseyNum}` : ''} ${gameDef?.label} ${filterOutGameDefType ? gameDef?.type.toUpperCase() : ''}`;

        const periodTime = !gameClockGoesDown
            ? ge.periodTime
            : Math.max(periodTimeDuration - ge.periodTime, 10);

        result.push({
            id: ge.id,
            definition,
            defId: ge.defId,
            eventType: EEventItemType.GAME,
            period: ge.period,
            frame: ge.frame,
            createdBy: ge.createdBy,
            teamId: ge.teamId,
            periodTime,
            playerId: ge.playerId,
            frameRate,
        });
    }

    return result;
}

/**
     * Resolves the event adjacent to the currently selected one within the (team-filtered) list.
     *
     * The selected event is resolved against the unfiltered list, so it remains known even after
     * team filtering removes it from the visible list. When the selected event is no longer present,
     * its position is located by the same stable ordering used to sort events (period, then frame),
     * and the neighbour in the requested direction is returned. This avoids relying on a stored index,
     * which becomes meaningless once filtering changes the contents of the list.
     *
     * @param events The currently visible (filtered) events, sorted by period then frame.
     * @param selectedEvent The full selected event, or undefined if nothing is selected.
     * @param direction 1 to move down the list, -1 to move up.
     */
export const findAdjacentEvent = (
    events: IGamePlayerShiftEvent[],
    selectedEvent: IGamePlayerShiftEvent | undefined,
    direction: 1 | -1
): IGamePlayerShiftEvent | undefined => {
    if (events.length === 0) {
        return;
    }

    if (!selectedEvent) {
        return direction === 1 ? events[0] : events[events.length - 1];
    }

    const currentIndex = events.findIndex(event => event.id === selectedEvent.id);
    if (currentIndex !== -1) {
        return events[currentIndex + direction];
    }

    return findAdjacentToRemovedEvent(events, selectedEvent, direction);
};

/**
 * Resolves the neighbour for a selected event that is no longer in the (filtered) list, by
 * locating where it would sit in the stable ordering (period, then frame).
 */
export const findAdjacentToRemovedEvent = (
    events: IGamePlayerShiftEvent[],
    selectedEvent: IGamePlayerShiftEvent,
    direction: 1 | -1
): IGamePlayerShiftEvent | undefined => {
    const firstAfterIndex = events.findIndex(event => compareEventOrder(event, selectedEvent) > 0);

    if (direction === 1) {
        return firstAfterIndex === -1 ? undefined : events[firstAfterIndex];
    }

    const boundary = firstAfterIndex === -1 ? events.length : firstAfterIndex;
    return events[boundary - 1];
};

/**
 * Compares two events using the same ordering applied when the list is sorted
 * (period, then frame, then event type). Keeping this in sync with that sort is
 * what lets us locate a filtered-out event's neighbour without skipping events
 * that share the same period and frame.
 */
export const compareEventOrder = (a: IGamePlayerShiftEvent, b: IGamePlayerShiftEvent): number => {
    return a.period - b.period
        || a.frame - b.frame
        || eventTypeOrder[a.eventType] - eventTypeOrder[b.eventType];
};
